"use client";

import type { CSSProperties, ReactNode } from "react";
import Image from "next/image";

// ---- Logo ----
export const MoondreamMark = ({ size = 28 }: { size?: number }) => (
  <Image
    src="/images/moondream_logo_color.png"
    width={size}
    height={size}
    alt=""
    aria-hidden="true"
    style={{ display: "block", objectFit: "contain" }}
  />
);

export const MoondreamWordmark = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <MoondreamMark size={22} />
    <span style={{ fontWeight: 600, fontSize: 17, letterSpacing: 0 }}>
      Moondream
    </span>
  </div>
);

// ---- Minimal icon set (Lucide-style, 1.75 stroke) ----
export type IconProps = {
  size?: number;
  style?: CSSProperties;
  children?: ReactNode;
};

const Icon = ({
  size = 18,
  viewBox = "0 0 24 24",
  children,
  style,
}: IconProps & { viewBox?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const IconArrowRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </Icon>
);
export const IconArrowUpRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7 17 17 7" />
    <path d="M8 7h9v9" />
  </Icon>
);
export const IconCheck = (p: IconProps) => (
  <Icon {...p}>
    <path d="m5 12 5 5L20 7" />
  </Icon>
);
export const IconCopy = (p: IconProps) => (
  <Icon {...p}>
    <rect x="8" y="8" width="13" height="13" rx="2" />
    <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
  </Icon>
);
export const IconPlay = (p: IconProps) => (
  <Icon {...p}>
    <polygon points="6,4 20,12 6,20" fill="currentColor" stroke="none" />
  </Icon>
);
export const IconPause = (p: IconProps) => (
  <Icon {...p}>
    <rect x="6" y="5" width="4" height="14" fill="currentColor" stroke="none" />
    <rect x="14" y="5" width="4" height="14" fill="currentColor" stroke="none" />
  </Icon>
);
export const IconGithub = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 19c-4.3 1.4-4.3-2.5-6-3m12 5v-3.5c0-1 .1-1.4-.5-2 2.8-.3 5.5-1.4 5.5-6.2a4.6 4.6 0 0 0-1.3-3.2 4.2 4.2 0 0 0-.1-3.2s-1.1-.3-3.5 1.3a12 12 0 0 0-6.2 0C6.5 2.7 5.4 3 5.4 3a4.2 4.2 0 0 0-.1 3.2 4.6 4.6 0 0 0-1.3 3.2c0 4.7 2.7 5.9 5.5 6.2-.6.6-.6 1.2-.5 2V21" />
  </Icon>
);
export const IconBolt = (p: IconProps) => (
  <Icon {...p}>
    <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
  </Icon>
);
export const IconCpu = (p: IconProps) => (
  <Icon {...p}>
    <rect x="5" y="5" width="14" height="14" rx="2" />
    <rect x="9" y="9" width="6" height="6" />
    <path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
  </Icon>
);
export const IconCloud = (p: IconProps) => (
  <Icon {...p}>
    <path d="M17.5 19a5 5 0 0 0 .4-9.7 7 7 0 0 0-13.4 2 4.5 4.5 0 0 0 .4 8.7h12.6z" />
  </Icon>
);
export const IconHeadset = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 17v-3a9 9 0 0 1 18 0v3" />
    <path d="M21 17a2 2 0 0 1-2 2h-1v-5h1a2 2 0 0 1 2 2v1zM3 17a2 2 0 0 0 2 2h1v-5H5a2 2 0 0 0-2 2v1z" />
  </Icon>
);
export const IconTarget = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </Icon>
);
export const IconSliders = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
  </Icon>
);
export const IconGlobe = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </Icon>
);
export const IconSun = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Icon>
);
export const IconMoon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </Icon>
);
export const IconMenu = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </Icon>
);
export const IconX = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);
export const IconChevron = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
);
export const IconScan = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 12h10" />
  </Icon>
);
export const IconBox = (p: IconProps) => (
  <Icon {...p}>
    <path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <path d="M3.3 7 12 12l8.7-5M12 22V12" />
  </Icon>
);

// ---- Code with tokens ----
type Token = string | [string, string];

export const Code = ({
  tokens,
  lang = "python",
}: {
  tokens: Token[];
  lang?: string;
}) => (
  <div className="code-block">
    <span className="lang-tag">{lang}</span>
    <pre>
      {tokens.map((t, i) => {
        if (typeof t === "string") return <span key={i}>{t}</span>;
        const [type, text] = t;
        return (
          <span key={i} className={type ? `tok-${type}` : ""}>
            {text}
          </span>
        );
      })}
    </pre>
  </div>
);
